import React, { useState, useEffect, useMemo } from "react";
import CopyToClipboard from 'react-copy-to-clipboard';
import { Card, Form, Input, Select, Divider, Row, Col, Typography, Modal, Button, notification } from "antd";
import { MailOutlined, MessageOutlined, PhoneOutlined, WalletOutlined } from "@ant-design/icons";
import { number_to_price, formatPriceNoDecimal } from "helper/common";
import { useTranslation } from "react-i18next";
import { CardOrderSummary } from "./OrderCards";
import OrderService from "services/order";
import { THIRDPARTY_CODE_IMAGE, THIRDPARTY_CODE } from "constants/app";
import { PAYMENT_METHOD_LABEL } from 'constants/order';
import QRCodeFrameSVG from '../../../../assets/icons/QRCodeFrame.svg';

const { Text } = Typography;

// Thông tin thanh toán
export const OrderPaymentInfo = ({ userData, paymentMethod, subtotal, tax, fee, total, useCard = true, qrResult = null, paymentMethodObj = null, qrLoading = false, loadingText = null, selectedPaymentMethodId = null, orderId = null, paymentMethodType = null, enableQrDisplay = 0 }) => {
  const { t: translation } = useTranslation();

  const [qrExpiryTimeLeft, setQrExpiryTimeLeft] = useState(60);
  const [isQrExpired, setIsQrExpired] = useState(false);
  const [isQrRefreshing, setIsQrRefreshing] = useState(false);

  // Kiểm tra xem hiển thị QR có được bật không
  const isQRDisplay = paymentMethodType === 1 && enableQrDisplay === 1;

  // Lấy URL ảnh QR
  const getQrImageUrl = (qrResult) => {
    return qrResult?.paymentData?.paymentQRImage || null;
  };

  const qrImageUrl = getQrImageUrl(qrResult);
  const hasQr = qrResult?.paymentData?.paymentQRImage;
  const shareLink = qrResult?.paymentData?.paymentLinkUrl || qrImageUrl;
  const isZaloPay = paymentMethodType === 5;

  // Cấu hình theo payment method type
  const qrConfig = useMemo(() => {
    if (isZaloPay) {
      return {
        duration: 900, // 15 phút
        buttonText: "Cập nhật mã QR",
        displayText: "Thời gian còn lại:",
        showTimerAlways: false
      };
    } else if (isQRDisplay) {
      return {
        duration: 60, // 60 giây
        buttonText: translation("order.reDisplayOnDevice"),
        displayText: translation("order.deviceDisplaying"),
        showTimerAlways: false
      };
    }
    return null;
  }, [isZaloPay, isQRDisplay, translation]);

  useEffect(() => {
    if (!qrConfig || !hasQr || isQrExpired) return;

    const expiryTime = Date.now() + (qrConfig.duration * 1000);
    setQrExpiryTimeLeft(qrConfig.duration);
    setIsQrExpired(false);
    let timer;

    const tick = () => {
      const now = Date.now();
      const remaining = Math.max(Math.ceil((expiryTime - now) / 1000), 0);
      setQrExpiryTimeLeft(remaining);
      if (remaining > 0) {
        timer = setTimeout(tick, 1000);
      } else {
        setIsQrExpired(true);
      }
    };
    timer = setTimeout(tick, 1000);
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [qrConfig?.duration, hasQr, qrResult, paymentMethodType]);

  const refreshQRAndResetTimer = async () => {
    if (!orderId || isQrRefreshing || !qrConfig) return false;

    setIsQrRefreshing(true);
    try {
      const resp = await OrderService.getPaymentInfo({
        id: orderId,
        paymentMethodId: qrResult?.paymentData?.paymentMethodId,
        paymentMethodType: paymentMethodType,
      });
      
      if (resp) {
        setQrExpiryTimeLeft(qrConfig.duration);
        setIsQrExpired(false);
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error refreshing QR:', error);
      return false;
    } finally {
      setIsQrRefreshing(false);
    }
  };

  const handleRefreshQR = async () => {
    await refreshQRAndResetTimer();
  };

  // Nếu người dùng chọn tiền mặt, hiển thị trạng thái thanh toán tiền mặt (không có QR)
  if (selectedPaymentMethodId === 'cash') {
    const cashContent = (
      <div className="op-payment-info-center">
        <div className="op-no-payment-info">{translation("order.cashPaymentInProgress")}</div>
      </div>
    );
    if (useCard) {
      return <Card className="op-payment-info-card" title={translation("order.paymentInfo")}>{cashContent}</Card>;
    }
    return cashContent;
  }

  const content = (
    <div className="op-payment-info-center">
      {qrLoading ? (
        <div className="op-qr-loading">{loadingText || translation("order.loadingQR")}</div>
      ) : hasQr ? (
        <div className="op-qr-full-container">
          <div className="op-qr-wrapper">
            <div className="op-qr-container">
              <div className={`op-qr-image-container ${isZaloPay && isQrExpired ? 'zalopay-expired' : ''}`}>
                {/* QR image */}
                {qrImageUrl ? (
                  <img
                    src={qrImageUrl}
                    alt="QR Code"
                    className="op-qr-image"
                    onError={(e) => {
                      console.error('QR image failed to load:', qrImageUrl);
                      e.target.style.display = 'none';
                    }}
                  />
                ) : (
                  <div className="op-no-qr">{translation("order.noQR")}</div>
                )}
                {/* QR Code frame SVG */}
                <img src={QRCodeFrameSVG} alt="QR Frame" className="op-qr-frame-svg" />
              </div>

              {!isZaloPay && (
                <div className="op-qr-details">
                  {paymentMethodObj?.raw?.paymentMethodReferName === "VietQR" ? (
                    <img
                      src={THIRDPARTY_CODE_IMAGE[THIRDPARTY_CODE.BANKING_VIETQR]}
                      alt={paymentMethodObj.name}
                      className="op-payment-logo"
                    />
                  ) : paymentMethodObj?.logoUrl ? (
                    <img
                      src={paymentMethodObj.logoUrl}
                      alt={paymentMethodObj.name}
                      className="op-payment-logo"
                    />
                  ) : null}

                  {/* Thông tin thanh toán*/}
                  <div className="op-receiver-name">
                    {paymentMethodObj?.raw?.paymentMethodReceiverName || ''}
                  </div>

                  <div className="op-account-number">
                    {paymentMethodObj?.raw?.paymentMethodIdentityNumber || ''}
                  </div>

                  <div className="op-bank-name">
                    {paymentMethodObj?.raw?.paymentMethodBankCode || paymentMethodObj?.raw?.paymentMethodReferName || paymentMethodObj?.name || ''}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* QR expiry countdown and refresh - dùng chung cho VietQR và ZaloPay */}
          {qrConfig && hasQr && !isQrExpired ? (
            <div className="op-qr-expiry-info">
              <div className="op-qr-timer">
                <div className="op-qr-timer-label">{qrConfig.displayText}</div>
                <div className="op-qr-timer-value">
                  {isZaloPay 
                    ? `${Math.floor(qrExpiryTimeLeft / 60)}:${(qrExpiryTimeLeft % 60).toString().padStart(2, '0')}`
                    : `${qrExpiryTimeLeft}s`
                  }
                </div>
              </div>
            </div>
          ) : qrConfig && hasQr && isQrExpired ? (
            <div className="op-qr-expiry-expired">
              <Button
                type="primary"
                onClick={handleRefreshQR}
                loading={isQrRefreshing}
                disabled={isQrRefreshing}
                className="op-refresh-qr-button"
              >
                {qrConfig.buttonText}
              </Button>
            </div>
          ) : null}

          {/* Link and share row - full width below QR */}
          <div className="op-share-section">
            <div className="op-link-input-container">
              <input
                readOnly
                onFocus={(e) => e.target.blur()}
                value={shareLink}
                className="op-link-input"
              />
              <CopyToClipboard text={shareLink} onCopy={(text, result) => {
                if (result) {
                  notification['success']({
                    message: "",
                    description: translation("order.linkCopied")
                  });
                } else {
                  notification['error']({
                    message: "",
                    description: translation("order.copyError")
                  });
                }
              }}>
                <button className="op-copy-button">Sao chép</button>
              </CopyToClipboard>
            </div>

            <div className="op-share-buttons">
              <div className="op-share-label">Gửi mã cho khách:</div>

              <div className="op-share-actions">
                <button className="op-share-button" onClick={() => {
                  const link = shareLink;
                  if (link) {
                    navigator.clipboard.writeText(link).then(() => {
                      window.open('https://chat.zalo.me', '_blank');
                    });
                  }
                }}><MessageOutlined /> Zalo</button>

                <button className="op-share-button" onClick={() => {
                  const link = shareLink;
                  if (link) {
                    window.location.href = `mailto:?subject=${encodeURIComponent('Mã thanh toán QR')}&body=${encodeURIComponent('Vui lòng click vào link dưới để xem mã QR thanh toán:\n' + link)}`;
                  }
                }}><MailOutlined /> Email</button>

                {window.matchMedia('(max-width: 767px) and (pointer: coarse)').matches && (
                  <button className="op-share-button" onClick={() => {
                    const link = qrImageUrl;
                    window.location.href = `sms:?body=${encodeURIComponent('Mã QR thanh toán: ' + link)}`;
                  }}><PhoneOutlined /> SMS</button>
                )}
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="op-no-payment-info">{translation("order.noPaymentInfo")}</div>
      )}
    </div>
  );

  if (useCard) {
    return (
      <Card className="op-payment-info-card" title={translation("order.paymentInfo")}>{content}</Card>
    );
  }

  return content;
};

// Thông tin chi tiết đơn hàng
export const OrderPaymentDetails = ({ selectedProducts, userData, subtotal, tax, fee, total, orderCode, createdAt }) => {
  // Tái sử dụng layout `CardOrderSummary` cho trang thanh toán (ẩn nút hành động)
  return (
    <CardOrderSummary
      selectedProducts={selectedProducts}
      subtotal={subtotal}
      tax={tax}
      fee={fee}
      total={total}
      showActions={false}
      isPaymentPage={true}
      orderCode={orderCode}
      createdAt={createdAt}
      customerData={userData}
    />
  );
};

// Cột trái: danh sách phương thức thanh toán
export const PaymentMethodsColumn = ({ paymentMethods = [], selectedPaymentMethodId = null, onSelect = () => {}, goToSettings = () => {}, isLoading = false }) => {
  const { t: translation } = useTranslation();

  const handleItemClick = (pm) => {
    if (isLoading || pm.id === selectedPaymentMethodId) return;
    onSelect(pm);
  };

  return (
    <div className="op-payment-method-card">
      <div className="co-card-title">{translation("order.paymentMethod")}</div>
      <div className="op-payment-method-content">
        <div className="op-payment-method-list">
          {paymentMethods.length === 0 && (
            <div>{translation("order.noPaymentMethod")}</div>
          )}

          {paymentMethods.map((pm) => (
            <div
              key={pm.id || pm.paymentMethodType}
              onClick={() => handleItemClick(pm)}
              className={`op-payment-method-item ${selectedPaymentMethodId === pm.id ? 'selected' : ''} ${isLoading ? 'disabled' : ''}`}
              style={{ pointerEvents: (isLoading || selectedPaymentMethodId === pm.id) ? 'none' : 'auto', opacity: isLoading ? 0.6 : 1 }}
            >
              <div className="op-payment-method-logo-container">
                {pm.id === 'cash' ? (
                  <div className="op-payment-method-logo-icon"><WalletOutlined /></div>
                ) : pm.raw?.paymentMethodReferName === "VietQR" ? (
                  <img src={THIRDPARTY_CODE_IMAGE[THIRDPARTY_CODE.BANKING_VIETQR]} alt={pm.name} className="op-payment-method-logo" />
                ) : pm.logoUrl ? (
                  <img src={pm.logoUrl} alt={pm.name} className="op-payment-method-logo" />
                ) : (
                  <div className="op-payment-method-logo-placeholder"></div>
                )}
              </div>
              <div className="op-payment-method-details">
                <div className="op-payment-method-name">
                  {pm.id === 'cash' ? `Thanh toán bằng ${pm.name}` : `Cổng thanh toán ${pm.name}`}
                </div>
              </div>
            </div>
          ))}

          {/* If only cash is available, show link to settings to add more methods */}
          {paymentMethods.length === 1 && paymentMethods[0]?.id === 'cash' && (
            <div className="op-add-method-section">
              <div className="op-add-method-text">{translation("order.addPaymentMethod")}</div>
              <button type="button" className="op-settings-button" onClick={goToSettings} disabled={isLoading}>{translation("order.goToPaymentSettings")}</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Cột trái: thông tin thanh toán
export const PaymentInfoColumn = ({ userData, paymentMethod, subtotal, tax, fee, total, qrResult, paymentMethodObj, qrLoading, loadingText = null, selectedPaymentMethodId, orderId = null, paymentMethodType = null, enableQrDisplay = 0 }) => {
  const { t: translation } = useTranslation();
  return (
    <div className="co-card-wrap">
      <div className="co-card-title">{translation("order.paymentInfo")}</div>
      <div className="op-info-column">
        <OrderPaymentInfo
          userData={userData}
          paymentMethod={paymentMethod}
          subtotal={subtotal}
          tax={tax}
          fee={fee}
          total={total}
          useCard={false}
          qrResult={qrResult}
          paymentMethodObj={paymentMethodObj}
          qrLoading={qrLoading}
          loadingText={loadingText}
          selectedPaymentMethodId={selectedPaymentMethodId}
          orderId={orderId}
          paymentMethodType={paymentMethodType}
          enableQrDisplay={enableQrDisplay}
        />
      </div>
    </div>
  );
};

// Cột phải: tóm tắt thanh toán
export const PaymentSummaryColumn = ({ selectedProducts, userData, subtotal, tax, fee, total, orderCode, createdAt }) => {
  return (
    <div className="op-summary-column">
      <OrderPaymentDetails
        selectedProducts={selectedProducts}
        userData={userData}
        subtotal={subtotal}
        tax={tax}
        fee={fee}
        total={total}
        orderCode={orderCode}
        createdAt={createdAt}
      />
    </div>
  );
};

// Modal xác nhận thanh toán tiền mặt
export const ConfirmCashModal = ({ isModalVisible, onCancel, onSuccess, orderId, orderCode, selectedPaymentMethodId }) => {
  const { t: translation } = useTranslation();

  const canConfirm = Boolean(selectedPaymentMethodId);
  const orderCreated = Boolean(orderId);

  const confirmClass = orderCreated ? 'ps-modal-save-btn' : 'co-button-disabled ps-modal-save-btn';
  const displayOrderCode = orderCode ? `#${orderCode}` : (orderId ? `#${orderId}` : '');

  return (
    <Modal
      title={translation("order.confirmPaymentTitle")}
      open={isModalVisible}
      onCancel={onCancel}
      footer={null}
      width={600}
      className="confirm-modal"
    >
      <div className="ps-modal-body-text">
        <Text className="ps-body-text">
          {translation("order.confirmPaymentMessage").replace('{orderCode}', displayOrderCode)}
        </Text>
      </div>
      <div className="ps-modal-actions">
        <Button 
          onClick={onCancel} 
          className="ps-modal-cancel-btn"
        >
          {translation("cancel")}
        </Button>
        <Button 
          type="primary"
          onClick={onSuccess} 
          disabled={!canConfirm}
          className={confirmClass}
        >
          {translation("order.confirmPayment")}
        </Button>
      </div>
    </Modal>
  );
};

// Modal xác nhận thanh toán QR thành công
export const ConfirmQRSuccessModal = ({ isModalVisible, onCancel, onSuccess, paymentMethod, paymentMethodId, total, loading, history, customerData = {}, onClose }) => {
  const { t: translation } = useTranslation();

  const handleViewHistory = () => {
    const basePath = history.location.pathname.split('?')[0];
    history.replace(`${basePath}?tab=2`);
    onClose();
  };

  const handleCreateNewOrder = () => {
    const basePath = history.location.pathname.split('?')[0];
    history.push(`${basePath}?tab=1`, { 
      customerData: {
        customerName: customerData?.firstName || "",
        customerPhone: customerData?.phoneNumber || "",
        address: customerData?.userHomeAddress || "",
        cccd: customerData?.appUserIdentity || "",
      }
    });
    onClose();
  };

  const displayPaymentMethod = (() => {
    if (paymentMethodId !== undefined && paymentMethodId !== null && PAYMENT_METHOD_LABEL.hasOwnProperty(paymentMethodId)) {
      return PAYMENT_METHOD_LABEL[Number(paymentMethodId)];
    }
    if (typeof paymentMethod === 'string' && /tiền mặt|cash/i.test(paymentMethod)) {
      return PAYMENT_METHOD_LABEL[0];
    }
    return paymentMethod || "";
  })();

  return (
    <Modal
      title={
        <div className="ps-modal-title">{translation("order.paymentSuccess")}</div>
      }
      open={isModalVisible}
      onCancel={handleCreateNewOrder}
      footer={null}
      centered
      className="confirm-modal"
    >
      <div className="ps-modal-body-text">
        <Typography.Text className="ps-body-text">
          {`${displayPaymentMethod} với số tiền ${formatPriceNoDecimal(total)} đã thành công.`}
        </Typography.Text>
      </div>

      <div className="ps-modal-actions">
        <Button 
          onClick={handleViewHistory} 
          className="ps-modal-cancel-btn"
        >
          {translation("order.viewHistory")}
        </Button>
        <Button 
          type="primary"
          onClick={handleCreateNewOrder}
          className="ps-modal-save-btn"
        >
          {translation("order.createNewOrder")}
        </Button>
      </div>
    </Modal>
  );
};

export default {
  OrderPaymentInfo,
  OrderPaymentDetails,
  PaymentMethodsColumn,
  PaymentInfoColumn,
  PaymentSummaryColumn,
};
